import MarkdownIt from 'markdown-it';
import Token from 'markdown-it/lib/token';

export const b64DecodeUnicode = (str: string): string => {
  try {
    const trimmedStr = str.trim().replace(/_/g, '/').replace(/-/g, '+');
    const paddedStr = trimmedStr.padEnd(trimmedStr.length + ((4 - (trimmedStr.length % 4)) % 4), '=');
    return decodeURIComponent(
      atob(paddedStr)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  } catch (error) {
    console.error('Failed to decode Base64 string:', error);
    return '';
  }
};

export class GetMessageTask {
  private fn: () => void;
  private controller: AbortController;
  private resultPromise: void | null = null;
  private aborted = false;

  constructor(fn: () => void, controller: AbortController) {
    this.fn = fn;
    this.controller = controller;
  }

  run() {
    this.aborted = false;
    this.resultPromise = this.fn.call(this);
    return this.resultPromise;
  }

  abort() {
    if (this.aborted) {
      return;
    }
    this.aborted = true;
    this.controller.abort();
  }
}

interface DeltaOp {
  insert: string | object;
  attributes?: {
    bold?: boolean;
    italic?: boolean;
    link?: string;
    code?: boolean;
  };
}

interface ListItemOp {
  type: string;
  params: {
    indent: number;
    delta: {
      ops: DeltaOp[];
    };
  };
}

interface GeneralTextItem {
  type: string;
  initParams?: {
    'data-language'?: string;
    'delta': {
      ops: DeltaOp[];
    };
  };
  items?: {
    type: string;
    params: {
      indent: number;
      delta: {
        ops: DeltaOp[];
      };
    };
  }[];
}

const deltaToString = (deltaOps: DeltaOp[]) => {
  let text = '';
  deltaOps.forEach((op) => {
    if (op.insert) {
      text += op.insert;
    }
  });
  return text;
};

const md = new MarkdownIt();

type Element = {
  type: string;
  initParams: {
    'data-language'?: string;
    'delta': {
      ops: DeltaOp[];
    };
  };
};
type List = {
  type: string;
  items: {
    type: string;
    params: {
      indent: number;
      delta: {
        ops: DeltaOp[];
      };
    };
  }[];
};

const convertChildren = (tokens: Token[]): DeltaOp[] => {
  const delta: DeltaOp[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    switch (token.type) {
      case 'text':
        delta.push({ insert: token.content || '' });
        break;
      case 'fence':
        delta.push({ insert: token.content || '' });
        break;
      case 'strong_open':
        i++;
        delta.push({ insert: tokens[i].content || '', attributes: { bold: true } });
        i++;
        break;
      case 'em_open':
        i++;
        delta.push({ insert: tokens[i].content || '', attributes: { italic: true } });
        i++;
        break;
      case 'link_open':
        i += 2;
        if (token.attrs) {
          delta.push({ insert: tokens[i - 1].content || '', attributes: { link: token.attrs[0][1] } });
        }
        break;
      case 'hardbreak':
        delta.push({ insert: { linebreak: 's' } });
        break;
      case 'softbreak':
        delta.push({ insert: { linebreak: 's' } });
        break;
      case 'code_inline':
        delta.push({ insert: token.content || '', attributes: { code: true } });
        break;
      default:
        delta.push({ insert: token.content || '' });
        break;
    }
  }
  return delta;
};
const fixMarkdown = (markdown: string): string => {
  markdown = markdown.replace(/#/g, '# ').replace(/# #/g, '##').replace(/# #/g, '##').replace(/# {2}/g, '# ');
  return markdown;
};
export const convertMarkdownToDelta = (markdown: string): { text: GeneralTextItem[]; lististOps: ListItemOp[] } => {
  let processedMarkdown = markdown.trim();
  processedMarkdown = markdown.replace(/```markdown([\s\S]*?)```/g, '---\n$1\n---');
  markdown = fixMarkdown(processedMarkdown);
  const tokens = md.parse(markdown, {});
  const text: GeneralTextItem[] = [];
  const lististOps: ListItemOp[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if ('heading_open' === token.type) {
      let type = 'hLarge';
      if ('h2' === token.tag) {
        type = 'hMedium';
      } else if ('h3' === token.tag) {
        type = 'hSmall';
      }
      const element: Element = {
        type,
        initParams: {
          delta: {
            ops: [],
          },
        },
      };
      for (let j = i + 1; j < tokens.length; j++) {
        const nextToken = tokens[j];
        if ('heading_close' === nextToken.type) {
          i = j;
          break;
        }
        if ('inline' === nextToken.type && nextToken.children) {
          element.initParams.delta.ops = convertChildren(nextToken.children);
        } else {
          element.initParams.delta.ops = convertChildren([nextToken]);
        }
      }
      if (element.initParams.delta.ops.length) {
        text.push(element);
      }
    }
    if ('paragraph_open' === token.type) {
      const element: Element = {
        type: 'paragraph',
        initParams: {
          delta: {
            ops: [],
          },
        },
      };
      for (let j = i + 1; j < tokens.length; j++) {
        const nextToken = tokens[j];
        if ('paragraph_close' === nextToken.type) {
          i = j;
          break;
        }
        if ('inline' === nextToken.type && nextToken.children) {
          element.initParams.delta.ops = convertChildren(nextToken.children);
        } else {
          element.initParams.delta.ops = convertChildren([nextToken]);
        }
      }
      if (element.initParams.delta.ops.length) {
        text.push(element);
      }
    }
    if ('ordered_list_open' === token.type || 'bullet_list_open' === token.type) {
      const list = 'bullet_list_open' === token.type ? 'bullet' : 'ordered';
      const element: List = {
        type: 'list',
        items: [],
      };
      let indent = -1;
      while (tokens[i].type !== `${list}_list_close`) {
        const nextToken = tokens[i];
        // set correct indent for the list in case in an item has sub items
        if (tokens[i].type.match(/_list_open/)) {
          indent++;
        }
        if (tokens[i].type.match(/_list_close/)) {
          indent--;
        }
        let delta: DeltaOp[] = [];
        if ('inline' === nextToken.type && nextToken.children) {
          delta = convertChildren(nextToken.children);
        } else {
          delta = convertChildren([nextToken]);
        }
        if (delta.length) {
          // if delta is not empty, add it to the list of lististOps and element.items
          if (deltaToString(delta).trim()) {
            const item = {
              type: 'bullet_list_open' === token.type ? 'listItemBullet' : 'listItemNumber',
              params: {
                indent,
                delta: {
                  ops: delta,
                },
              },
            };
            lististOps.push(item);
            element.items.push(item);
          }
        }
        i++;
      }
      if (element.items.length) {
        text.push(element);
      }
    }
    if ('fence' === token.type) {
      const element: Element = {
        type: 'syntax',
        initParams: {
          'data-language': token.info,
          'delta': {
            ops: convertChildren([token]),
          },
        },
      };
      if (element.initParams.delta.ops.length) {
        text.push(element);
      }
    }
  }
  return { text, lististOps };
};
